import React from "react";
import { ChevronDown } from "lucide-react";
import { GroupDTO, SeriesDTO } from "@/generated-client";
import { Localizer } from "@/main/react/Types";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ClientFactory } from "@/main/common/ClientFactory";
import { cn } from "@/lib/utils";
import logger from "@/lib/logger";
import { GroupOverflowRow } from "@/main/react/discussion/DiscussionGroupSummary";
import { orderDiscussionSeries, SeriesOverflowRow } from "@/main/react/discussion/DiscussionSeriesSummary";

/**
 * U39 (`DISCUSSION-96`, `DISCUSSION-123`, S7 decision 2026-09-28, copy C56): when a post is BOTH shared in
 * groups and part of series, its provenance is ONE line, "In {0} groups · {1} series", instead of two
 * near-identical avatar rows that spent ~119 CSS px of the first phone screen and never said what kind of
 * place each one listed. The detail stays one tap away: the line opens a popover listing the groups (the
 * page already holds them) and the series (fetched on first open, the request `DiscussionSeriesSummary`
 * makes on mount).
 *
 * A post in only groups, or only series, keeps its existing single row; this line exists only for the
 * two-row case the decision is about.
 */
export default function DiscussionProvenanceSummary({
    groups,
    seriesCount,
    discussionPublicId,
    showPrivateAccessBadge = false,
    localizer,
    className,
}: {
    groups: GroupDTO[];
    seriesCount: number;
    discussionPublicId?: string;
    /** The group-only badge the group row carried; kept, so the merge drops no fact. */
    showPrivateAccessBadge?: boolean;
    localizer: Localizer;
    className?: string;
}) {
    const [series, setSeries] = React.useState<SeriesDTO[] | null>(null);
    const [open, setOpen] = React.useState(false);
    const requestedRef = React.useRef(false);

    React.useEffect(() => {
        if (!open || requestedRef.current || !discussionPublicId) {
            return;
        }
        requestedRef.current = true;
        let cancelled = false;
        const load = async () => {
            try {
                const response = await ClientFactory.getAjaxPaginationRestControllerApi().seriesForDiscussion(discussionPublicId, undefined);
                if (!cancelled) {
                    setSeries(orderDiscussionSeries(response?.data ?? []));
                }
            } catch (error) {
                // The groups half still renders; a failed series read leaves its half empty, never an
                // error box above a readable post (rule 32).
                logger.error("[DiscussionProvenanceSummary] failed to load the discussion's series", error);
                if (!cancelled) {
                    setSeries([]);
                }
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [discussionPublicId, open]);

    return (
        <div
            className={cn("flex min-w-0 flex-wrap items-center gap-2 text-sm text-muted-foreground", className)}
            data-playwright-id="discussion-provenance-summary"
        >
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger
                    type="button"
                    data-playwright-id="discussion-provenance-trigger"
                    className="inline-flex min-w-0 items-center gap-0.5 rounded-sm text-left font-medium text-text-main underline decoration-dotted underline-offset-2 touch-manipulation hover:decoration-solid [@media(pointer:coarse)]:min-h-11 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                    <span className="truncate">
                        {localizer("discussion.provenance.summary", String(groups.length), String(Math.max(0, seriesCount)))}
                    </span>
                    <ChevronDown className="size-3.5 shrink-0" aria-hidden="true" />
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    className="w-72 p-1"
                    data-playwright-id="discussion-provenance-menu"
                >
                    <div className="max-h-72 overflow-y-auto">
                        <div className="px-2 pb-1 pt-1.5 text-xs font-medium text-muted-foreground">
                            {localizer("discussion.section.groups")}
                        </div>
                        {groups.map((group, index) => (
                            <GroupOverflowRow key={group.publicId ?? `group-${index}`} group={group} localizer={localizer} />
                        ))}
                        <div className="px-2 pb-1 pt-2 text-xs font-medium text-muted-foreground">
                            {localizer("discussion.section.series")}
                        </div>
                        {(series ?? []).map((item) => (
                            <SeriesOverflowRow key={item.publicId} series={item} localizer={localizer} />
                        ))}
                    </div>
                </PopoverContent>
            </Popover>
            {showPrivateAccessBadge ? (
                <span className="rounded-full border border-primary/20 bg-primary/8 px-2.5 py-1 text-[0.65rem] font-medium uppercase tracking-[0.08em] text-primary">
                    {localizer("discussion.groups.summary.group_only")}
                </span>
            ) : null}
        </div>
    );
}
